import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
} from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ResponsiveService implements OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private readonly onDestroy = new Subject<void>();
  private readonly largeDesktopBreakpoint = '1600px';
  private readonly desktopBreakpoint = '1280px';
  private readonly tabletBreakpointMax = '1279.98px';
  private readonly tabletBreakpointMin = '649.98px';
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);

  // Exposed API
  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private isBrowser = isPlatformBrowser(this.platformId);

  /**
   * Responsive getters and streams
   */
  public get isMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.breakpointObserver.isMatched(`(max-width: ${this.tabletBreakpointMin})`);
  }

  public get isTablet(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.breakpointObserver.isMatched(`(min-width: ${this.tabletBreakpointMin}) and (max-width: ${this.tabletBreakpointMax})`);
  }

  public get isDesktop(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }
    return this.breakpointObserver.isMatched(`(min-width: ${this.desktopBreakpoint})`);
  }

  public get isLargeDesktop(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.breakpointObserver.isMatched(`(min-width: ${this.largeDesktopBreakpoint})`);
  }

  // Define as unified streams using a helper method
  public isMobileScreen$ = this.createBreakpointStream(`(max-width: ${this.tabletBreakpointMin})`, false);
  public isTabletScreen$ = this.createBreakpointStream(`(min-width: ${this.tabletBreakpointMin}) and (max-width: ${this.tabletBreakpointMax})`, false);
  public isDesktopScreen$ = this.createBreakpointStream(`(min-width: ${this.desktopBreakpoint})`, true); // Default true for SSR
  public isLargeDesktopScreen$ = this.createBreakpointStream(`(min-width: ${this.largeDesktopBreakpoint})`, false);

  // Helper to handle SSR vs Browser logic in one place
  private createBreakpointStream(query: string, defaultValue: boolean): Observable<boolean> {
    if (!this.isBrowser) {
      return of(defaultValue);
    }
    return this.breakpointObserver.observe(query).pipe(
      map(r => r.matches),
      startWith(this.breakpointObserver.isMatched(query)), // Immediate browser check
      shareReplay({bufferSize: 1, refCount: true}),
    );
  }

  public currentBreakpoint$: Observable<'largeDesktop' | 'desktop' | 'tablet' | 'mobile'> = combineLatest([
    this.isLargeDesktopScreen$,
    this.isDesktopScreen$,
    this.isTabletScreen$,
    this.isMobileScreen$,
  ]).pipe(
    map(([isLarge, isDesk, isTab, isMob]) => {
      if (isLarge) return 'largeDesktop';
      if (isDesk) return 'desktop';
      if (isTab) return 'tablet';
      if (isMob) return 'mobile';
      return 'desktop'; // Fallback
    }),
    distinctUntilChanged(), // Prevent unnecessary emissions
    shareReplay(1),
  );

  // UI state
  public sideNavToggle$ = new BehaviorSubject<boolean>(false);

  constructor() {
    // Only set initial toggle state on browser load
    if (this.isBrowser) {
      this.sideNavToggle$.next(!this.isMobile);
    }
  }

  /**
   * True when the main side navigation is open
   * Triggered by Angular Material in the main app template
   */
  public sideNavToggle(open: boolean): void {
    this.sideNavToggle$.next(open);
  }

}
